// Superadmin-sessie: starten, beëindigen en de stand uitlezen.
//
// De sessie leeft als custom claim in het ID-token. Twee dingen om te
// onthouden:
//
//   1. Een nieuwe claim komt pas in het token na een GEFORCEERDE refresh
//      (`getIdTokenResult(true)`). Zonder die refresh blijft het oude token
//      tot een uur hangen en krijg je "Missing or insufficient permissions"
//      terwijl de sessie server-side allang loopt.
//   2. Andersom geldt hetzelfde bij beëindigen — daarom wist deze hook het
//      token ook actief, en toetsen de rules bovendien de expiry ín de claim.
//
// AI-CONTEXT: See docs/features/superadmin-tenant-access.md

import { useCallback, useEffect, useState } from 'react'
import firebase from '../firebase/compat'
import { auth } from '../firebase/config'
import readSuperAdminSession from '../helpers/superAdmin/readSuperAdminSession'

const REGION = 'europe-west1'

const useSuperAdminSession = () => {

    const [session, setSession] = useState(null)
    const [loading, setLoading] = useState(true)

    const refresh = useCallback(async (force = false) => {
        const user = auth.currentUser

        if (!user) {
            setSession(null)
            setLoading(false)
            return null
        }

        const result = await user.getIdTokenResult(force)
        const current = readSuperAdminSession(result.claims, Date.now())

        setSession(current)
        setLoading(false)
        return current
    }, [])

    useEffect(() => {
        const unsub = auth.onAuthStateChanged(() => { refresh(false) })
        return () => unsub()
    }, [refresh])

    const startSession = useCallback(async ({ compagnyId, reason, minutes }) => {
        const start = firebase.app().functions(REGION).httpsCallable('startSuperAdminSession')
        const response = await start({ compagnyId, reason, minutes })

        await refresh(true)

        return response.data
    }, [refresh])

    const endSession = useCallback(async () => {
        const end = firebase.app().functions(REGION).httpsCallable('endSuperAdminSession')
        const response = await end({})

        await refresh(true)

        return response.data
    }, [refresh])

    return { session, loading, refresh, startSession, endSession }
}

export default useSuperAdminSession
